import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Avatar, Chip, Box, Stack, Typography } from "@mui/material";
import PendingSpopup from "../PendingSpopup";
import { IP } from "../../../../../internalServer";
import "./../../Review_student_list/reviewStudentList.css";

/* ---------- Helpers ---------- */

const resolveAvatar = (student_id) => {
  if (student_id?.profile_picture) {
    return IP + student_id.profile_picture.replace(/^\./, "");
  }
  if (student_id?.gender === "M") {
    return student_id?.avatarProfile
      ? `/images/Boys/${student_id.avatarProfile}.webp`
      : "/images/Student_boy_pic.png";
  }
  if (student_id?.gender === "F") {
    return student_id?.avatarProfile
      ? `/images/Girls/${student_id.avatarProfile}.webp`
      : "/images/Student_girl_pic.png";
  }
  return "/images/default image.png";
};

const fallbackAvatar = (student_id) => {
  if (student_id?.gender === "M") {
    return student_id?.avatarProfile
      ? `/images/Boys/${student_id.avatarProfile}.webp`
      : "/images/Student_boy_pic.png";
  }
  if (student_id?.gender === "F") {
    return student_id?.avatarProfile
      ? `/images/Girls/${student_id.avatarProfile}.webp`
      : "/images/Student_girl_pic.png";
  }
  return "/images/default image.png";
};

const formatDate = (a) => {
  const d = new Date(a);
  const day = d.getUTCDate();
  const month = d.getUTCMonth() + 1;
  const year = d.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

/* ---------- Status Chip ---------- */

const StatusChip = ({ status }) => {
  const map = {
    review: { label: "On Review", bg: "#fef3c7", color: "#b45309" },
    pending: { label: "Not Submitted", bg: "#fee2e2", color: "#b91c1c" },
    completed: { label: "Completed", bg: "#dcfce7", color: "#15803d" },
  };
  const cfg = map[status] || { label: "—", bg: "#f3f4f6", color: "#6b7280" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 11,
        height: 24,
        borderRadius: 1.5,
      }}
    />
  );
};

/* ---------- Student name cell ---------- */

const StudentNameCell = ({ student_id, clickable, onClick, to, state }) => {
  const avatar = (
    <Avatar
      src={resolveAvatar(student_id)}
      alt={student_id?.name}
      imgProps={{
        onError: (e) => {
          e.target.src = fallbackAvatar(student_id);
        },
      }}
      sx={{
        width: 32,
        height: 32,
        border: "2px solid #eef2ff",
        fontSize: 13,
        fontWeight: 700,
        bgcolor: "#e0e7ff",
        color: "#1d4ed8",
      }}
    >
      {student_id?.name?.charAt(0)?.toUpperCase()}
    </Avatar>
  );

  const content = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{ cursor: "pointer", textDecoration: "none", color: "inherit" }}
    >
      {avatar}
      <Typography
        fontSize={13}
        fontWeight={500}
        color="#1f2937"
        sx={{
          "&:hover": { color: "#1d4ed8" },
          transition: "color .15s",
          whiteSpace: "nowrap",
        }}
      >
        {student_id?.name}
      </Typography>
    </Stack>
  );

  if (!clickable) return content;

  return (
    <NavLink
      to={to}
      state={state}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      {content}
    </NavLink>
  );
};

/* ---------- Main row ---------- */

const StudentTable = ({ student, isAutoEval }) => {
  const [click, setClick] = useState(false);
  const { student_id, assessment_id, submitted_on, status, obtained_marks } =
    student;

  const isPending = status === "pending";

  const marksDisplay =
    obtained_marks || obtained_marks === 0 ? obtained_marks : "-";

  const marksColor =
    obtained_marks && assessment_id?.total_marks
      ? obtained_marks / assessment_id.total_marks >= 0.4
        ? "#15803d"
        : "#b91c1c"
      : "#6b7280";

  return (
    <>
      <tr
        style={{
          borderBottom: "1px solid #f3f4f6",
          transition: "background .15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        {/* Name */}
        <td style={{ textAlign: "left", padding: "12px 16px" }}>
          {!isPending ? (
            <StudentNameCell
              student_id={student_id}
              clickable
              to="/reviewallquestions"
              state={{
                assessmentId: assessment_id?.id,
                studentId: student_id?.id,
                isAutoEval,
                studentStatus: status,
              }}
            />
          ) : (
            <Box onClick={() => setClick(!click)}>
              <StudentNameCell student_id={student_id} clickable={false} />
            </Box>
          )}
        </td>

        {/* Roll No. */}
        <td style={{ padding: "12px 16px" }}>
          <Typography fontSize={13} color="#374151">
            {student_id?.roll_number || "—"}
          </Typography>
        </td>

        {/* Class */}
        <td style={{ padding: "12px 16px" }}>
          <Typography fontSize={13} color="#374151">
            {assessment_id?.standard?.name || "—"}
          </Typography>
        </td>

        {/* Division */}
        <td style={{ padding: "12px 16px" }}>
          <Typography fontSize={13} color="#374151">
            {assessment_id?.section?.name || "—"}
          </Typography>
        </td>

        {/* Date submitted */}
        <td style={{ padding: "12px 16px" }}>
          {submitted_on ? (
            <Typography fontSize={13} color="#374151">
              {formatDate(submitted_on)}
            </Typography>
          ) : (
            <Typography fontSize={12} color="#9ca3af" fontStyle="italic">
              Not submitted
            </Typography>
          )}
        </td>

        {/* Marks */}
        <td style={{ padding: "12px 16px" }}>
          <Typography fontSize={13} fontWeight={600} color={marksColor}>
            {marksDisplay}
            <span style={{ color: "#9ca3af", fontWeight: 400 }}>
              /{assessment_id?.total_marks}
            </span>
          </Typography>
        </td>

        {/* Status */}
        <td style={{ padding: "12px 16px" }}>
          <StatusChip status={status} />
        </td>
      </tr>

      {click && status === "pending" && (
        <PendingSpopup setPendingPopup1={setClick} />
      )}
    </>
  );
};

export default StudentTable;
